import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, combineLatest, iif, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
    debounceTime,
    filter,
    map,
    mergeMap,
    shareReplay,
    skip,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';

import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { SharedLanguageService } from '@language';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import { DroppanelStyle } from '../../../../elements/components/droppanel/entities/droppanel-style.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import { ShAutoFocusDirective } from '../../../../elements/directives/auto-focus/auto-focus.directive';
import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';
import { ShDynamicResizeDirective } from '../../../../elements/directives/dynamic-resize/dynamic-resize.directive';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { mod } from '../../../../helpers/modulo';
import { MultiValueFormInput } from '../../../../helpers/multi-value-form-input';
import {
    FormTagListStyle,
    FormTagListStyleModifier,
    FormTagListStyling
} from '../../../entities/form-tag-list-style.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { FormTagListActionItem, FormTagListActionItemLocation } from '../main/form-tag-list-action-item.class';

type SanitizeToStringFunction = (value: string, existingValues: string[]) => string;
type SanitizeToStringArrayFunction = (value: string, existingValues: string[]) => string[];

@Component({
    selector: 'sh-form-tag-list-complex-base',
    templateUrl: './form-tag-list-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormTagListComplexBaseComponent)],
    standalone: true,
    imports: [
        NgIf,
        NgTemplateOutlet,
        ShActionItemComponent,
        ReactiveFormsModule,
        NgFor,
        ShIconComponent,
        NgClass,
        AsyncPipe,
        ShAutoFocusDirective,
        ShClickOutsideDirective,
        ShDynamicResizeDirective
    ]
})
export class ShFormTagListComplexBaseComponent
    extends MultiValueFormInput<string>
    implements OnInit, OnDestroy, AfterViewChecked
{
    private readonly minTextLength: number = 1;
    private readonly debounceTime: number = 300;

    private readonly state$: BehaviorSubject<FormValue<string>[]> = new BehaviorSubject<FormValue<string>[]>([]);
    private readonly select$: Subject<string> = new Subject<string>();
    private readonly currentActionItem$: ReplaySubject<FormTagListActionItem> =
        new ReplaySubject<FormTagListActionItem>();

    private readonly duplicatesValuesFound$: BehaviorSubject<FormValue<string>[]> = new BehaviorSubject<
        FormValue<string>[]
    >([]);

    private readonly disabled$ = new BehaviorSubject<boolean>(false);

    public readonly tagInputControl: FormControl = new FormControl(null);

    private savedInput: FormValue<string>[] = [];

    public filteredState$!: Observable<FormValue<string>[]>;
    public hasDuplicateInput$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isIndeterminate$!: Observable<boolean>;
    public suggestions$!: Observable<string[]>;
    public typing$!: Observable<string>;

    public sizes: typeof IconSize = IconSize;
    public droppanelOpen = false;
    public addActionItem$!: Observable<FormTagListActionItem>;
    public actionLocation: typeof FormTagListActionItemLocation = FormTagListActionItemLocation;
    public formTagListStyle: typeof FormTagListStyle = FormTagListStyle;
    public formTagListStyleModifier: typeof FormTagListStyleModifier = FormTagListStyleModifier;
    public _maxLength: number = 32766; // Max allowed by Engine for simple fields

    public suggestionFocusIndex: number = -1;
    public tagFocusIndex: number = -1;

    public tagEditFormControl: FormControl<string | null> = new FormControl();
    public currentTagEditIndex: number = -1;

    @Input() describeLabelId!: string;
    @Input() invalid = false;
    @Input() id!: string;
    @Input() placeholder?: string;
    @Input() duplicateErrorMsg!: string;
    @Input() errorMsg!: string;
    @Input() indeterminateMsg = '';
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;
    @Input() styling!: FormTagListStyling;
    @Input() autocomplete = 'off';
    @Input() singleTag = false;
    @Input() editable: boolean = false;
    @Input() set maxLength(length: number) {
        if (length) {
            this._maxLength = Math.min(length, this._maxLength);
        }
    }

    @Input() caseSensitive = true;
    @Output() duplicateValues: EventEmitter<FormValue<string>[]> = new EventEmitter<FormValue<string>[]>();

    @Input() sanitizeFct: SanitizeToStringFunction | SanitizeToStringArrayFunction = val => val;
    @Input() autocompleteFct: (term: string) => Observable<string[]> = term => of();
    @Input() set actionItem(actionItem: FormTagListActionItem) {
        if (actionItem) {
            this.currentActionItem$.next(actionItem);
        }
    }

    @Input() set isDisabled(disabled: boolean) {
        this.disabled$.next(disabled);
        this.setDisabledState(disabled);
        if (disabled) {
            this.tagInputControl.disable();
        } else {
            this.tagInputControl.enable();
        }
    }

    @Output() internalError: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild('searchInput') searchElement?: ElementRef<HTMLElement>;
    @ViewChildren('suggestionItem') suggestionItems!: QueryList<ElementRef>;
    @ViewChildren('tagItem') tagItems!: QueryList<ElementRef>;

    // Injectors
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (!this.isActiveComponent()) return;

        if ((event.target as HTMLElement).tagName.toUpperCase() === 'INPUT') {
            return;
        }

        if (event.key === 'ArrowLeft' && this.tagFocusIndex >= 0) {
            event.preventDefault();
            this.tagFocusIndex = mod(this.tagFocusIndex - 1, this.tagItems.toArray().length);
            this.cdr.markForCheck();
        }

        if (event.key === 'ArrowRight' && this.tagFocusIndex >= 0) {
            event.preventDefault();
            this.tagFocusIndex = mod(this.tagFocusIndex + 1, this.tagItems.toArray().length);
            this.cdr.markForCheck();
        }

        if (event.key === 'Tab' || event.key === 'Escape') {
            this.tagFocusIndex = -1;
            this.currentTagEditIndex = -1;

            this.droppanelOpen = false;
            this.deactivate();
        }

        if (!this.droppanelOpen) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.suggestions$.pipe(take(1)).subscribe(suggestions => {
                this.suggestionFocusIndex = Math.min(this.suggestionFocusIndex + 1, suggestions.length - 1);
                this.cdr.markForCheck();
            });
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.suggestionFocusIndex = Math.max(this.suggestionFocusIndex - 1, -1);

            this.cdr.markForCheck();
        }
    }

    constructor(ref: ChangeDetectorRef) {
        super(ref);
    }

    ngOnInit(): void {
        this.initAddActionItemStream();
        this.initDuplicatesStream();
        this.initIndeterminateStream();
        this.initFilteredStateStream();
        this.initTypingStream();
        this.initSuggestions();
        this.initSelectStreamListener();
        this.initStateListener();

        this.tagInputControl.valueChanges.subscribe(_ => {
            if (this.tagInputControl.getError('maxlength')) {
                // Validator set by maxlength property
                this.internalError.emit(this.translateService.instant('errors.max-length', { limit: this._maxLength }));
            } else {
                this.internalError.emit('');
            }
        });
    }

    ngAfterViewChecked() {
        if (this.isActiveComponent()) {
            if (this.droppanelOpen) {
                this.focusSuggestions();
            }

            this.focusTags();
        }
    }

    private focusSuggestions(): void {
        if (this.suggestionFocusIndex === -1) {
            if (this.searchElement) {
                this.searchElement.nativeElement.focus();
            }
            return;
        }

        const items = this.suggestionItems.toArray();
        if (this.suggestionFocusIndex >= 0) {
            if (items[this.suggestionFocusIndex]) {
                items[this.suggestionFocusIndex].nativeElement.focus({ preventScroll: true });
                items[this.suggestionFocusIndex].nativeElement.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        }
    }

    private focusTags(): void {
        const items = this.tagItems.toArray();
        if (this.tagFocusIndex >= 0) {
            if (items[this.tagFocusIndex]) {
                items[this.tagFocusIndex].nativeElement.focus();
            }
        }
    }

    private initAddActionItemStream(): void {
        const actionItem$: Observable<FormTagListActionItem> = this.currentActionItem$.pipe(
            startWith(new FormTagListActionItem())
        );

        this.addActionItem$ = combineLatest([actionItem$, this.disabled$]).pipe(
            takeUntil(this.destroy$),
            map(([actionItem, disabled]) => ({
                ...actionItem,
                eventListener: (event: MouseEvent) => {
                    this.addTag();
                    actionItem.eventListener(event);
                },
                disabled
            }))
        );
    }

    private addTag(): void {
        if (typeof this.tagInputControl.value === 'string') {
            const value: string = this.tagInputControl.value.trim();

            if (value !== '' && this.tagInputControl.valid) {
                this.addValue(value);
                this.clearInputControl();
            }
        }

        this.closeDroppanel();
    }

    private addValue(value: string): void {
        const cleanValues: string[] = this.cleanupStringValue(value);

        for (const cleanValue of cleanValues) {
            if (cleanValue === '') {
                continue;
            }

            const found: FormValue<string> | undefined = this.currentFormValues.find(formValue =>
                this.caseSensitive
                    ? formValue.value === cleanValue
                    : formValue.value.toLowerCase() === cleanValue.toLowerCase()
            );

            if (typeof found !== 'undefined') {
                if (
                    found.state === FormValueState.SET ||
                    (found.state === FormValueState.INDETERMINATE && !this.caseSensitive)
                ) {
                    const duplicatesWithoutCleanValue: FormValue<string>[] = this.duplicatesValuesFound$.value.filter(
                        stateDuplicate => stateDuplicate.value !== cleanValue
                    );
                    // last emitted is always the last element in the BehaviorSubject, and thus emission
                    this.duplicatesValuesFound$.next([...duplicatesWithoutCleanValue, new FormValue(cleanValue)]);

                    return this.hasDuplicateInput$.next(true);
                }

                return this.changeStateIfExists(cleanValue, FormValueState.SET);
            }

            if (cleanValue !== '' && this.singleTag) {
                this.state$.next([new FormValue(cleanValue)]);
            } else {
                this.state$.next([...this.currentFormValues, new FormValue(cleanValue)]);
            }
        }
    }

    private cleanupStringValue(value: string): string[] {
        const sanitized: string | string[] = this.sanitizeFct(value, this.currentTags);
        const stringArray: string[] = Array.isArray(sanitized) ? sanitized : [sanitized];

        const trimmedValueWithoutDoubleSpaces: string[] = stringArray.map(val => val.replace(/\s{2,}/g, ' '));

        return trimmedValueWithoutDoubleSpaces;
    }

    private changeStateIfExists(value: string, newState: FormValueState): void {
        const newFormValues: FormValue<string>[] = [...this.currentFormValues];
        const tagIndex: number = this.currentTags.findIndex(tag => new RegExp(value, 'i').test(tag));

        if (tagIndex === -1) {
            return;
        }

        if (!this.caseSensitive) {
            this.state$.next([...newFormValues, new FormValue(value, newState)]);
            return;
        }

        const formValue: FormValue<string> = newFormValues[tagIndex];

        if (formValue.state !== newState) {
            newFormValues.splice(tagIndex, 1, new FormValue(formValue.value, newState));
            this.state$.next(newFormValues);
        }
    }

    private clearInputControl(): void {
        this.closeDroppanel();
        this.tagInputControl.setValue(null, { emitEvent: false });
    }

    private initDuplicatesStream(): void {
        this.tagInputControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.hasDuplicateInput$.next(false));

        this.duplicatesValuesFound$
            .pipe(takeUntil(this.destroy$))
            .subscribe(duplicates => this.duplicateValues.emit(duplicates));
    }

    private initIndeterminateStream(): void {
        this.isIndeterminate$ = this.state$.pipe(
            map(formValues => !!formValues.find(formValue => formValue.state === FormValueState.INDETERMINATE))
        );
    }

    private initFilteredStateStream(): void {
        this.filteredState$ = this.state$.pipe(
            map(formValues => formValues.filter(formValue => formValue.state === FormValueState.SET))
        );
    }

    private initTypingStream(): void {
        this.typing$ = this.tagInputControl.valueChanges;
    }

    private initSuggestions(): void {
        this.suggestions$ = this.typing$.pipe(
            debounceTime(this.debounceTime),
            mergeMap(term => iif(() => !term || term.length < this.minTextLength, of([]), this.autocomplete$(term))),
            shareReplay(1),
            tap(val => {
                if (val.length === 0) {
                    this.closeDroppanel();
                } else {
                    this.openDroppanel();
                }
            })
        );
    }

    private initSelectStreamListener(): void {
        this.select$.subscribe(selectedValue => {
            this.addValue(selectedValue);
            this.clearInputControl();
        });
    }

    private initStateListener(): void {
        this.state$.pipe(takeUntil(this.destroy$)).subscribe(result => {
            this.currentTagEditIndex = -1;
            this.propagateChange(result);
        });
    }

    ngOnDestroy(): void {
        this.state$.complete();
        this.select$.complete();
        this.currentActionItem$.complete();
        this.hasDuplicateInput$.complete();
    }

    public writeValueImpl(input: FormValue<string>[]): void {
        /*  superclass ensures that it's an FormValue<any>[],
        here we need to check that value is string */
        if (!input.every(formValue => typeof formValue.value === 'string')) {
            console.error('ShFormTagListComplexBaseComponent:: value must be Array<FormValue<string>>', input);
            return this.writeValueImpl([]);
        }

        this.savedInput = input;
        this.state$.next(input);
    }

    public onEnter(event: Event): void {
        event.preventDefault();
        this.addTag();
    }

    public onBackspace(): void {
        if (this.styling.style !== FormTagListStyle.Embedded) {
            return;
        }

        if (this.tagInputControl.value === null) {
            this.filteredState$
                .pipe(
                    take(1),
                    filter(values => values.length > 0),
                    map(values => values.map(value => value.value)),
                    map(values => values[values.length - 1])
                )
                .subscribe(value => this.removeTag(value));
        }

        if (this.tagInputControl.value === '') {
            this.tagInputControl.setValue(null, { emitEvent: false });
        }
    }

    public onSelect(value: string): void {
        this.select$.next(value);
        this.closeDroppanel();
    }

    public removeTag(value: string, restoreToInput?: boolean): void {
        const tagIndex: number = this.currentTags.indexOf(value);
        const newFormValues: FormValue<string>[] = [...this.currentFormValues];

        if (tagIndex === -1) {
            return;
        }

        const foundFormValue: FormValue<string> | undefined = this.savedInput.find(
            formValue => formValue.value === value
        );

        if (typeof foundFormValue !== 'undefined') {
            // Only UNSET removed SET values, keep saved state if not SET. eg. Set back to INDETERMINATE
            const newState: FormValueState =
                foundFormValue.state === FormValueState.SET ? FormValueState.UNSET : foundFormValue.state;
            newFormValues.splice(tagIndex, 1, new FormValue(foundFormValue.value, newState));
        } else {
            newFormValues.splice(tagIndex, 1);
        }

        const duplicatesWithoutToRemove: FormValue<string>[] = this.duplicatesValuesFound$.value.filter(
            stateDuplicate => !new RegExp(value, 'i').test(stateDuplicate.value)
        );

        this.duplicatesValuesFound$.next(duplicatesWithoutToRemove);

        if (restoreToInput) {
            this.tagInputControl.setValue(value, { emitEvent: false });
        }

        this.state$.next(newFormValues);
        this.tagInputControl.updateValueAndValidity();
    }

    public onSearchFocus(): void {
        this.suggestionFocusIndex = -1;
        this.tagFocusIndex = -1;
        this.activate();
    }

    public onTagFocus(): void {
        if (this.tagFocusIndex === -1) {
            this.tagFocusIndex = 0;
        }
        this.activate();
    }

    public preventDefault(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    // Suggestions
    private autocomplete$(term: string): Observable<string[]> {
        if (!term) {
            return of([]);
        }

        return of([]).pipe(switchMap(() => this.autocompleteFct(term).pipe(takeUntil(this.typing$.pipe(skip(1))))));
    }

    // drop panel
    public toggleDroppanel(): void {
        this.droppanelOpen = !this.droppanelOpen;
    }

    public closeDroppanel(): void {
        this.droppanelOpen = false;
    }

    public openDroppanel(): void {
        this.droppanelOpen = true;
    }

    public onEditMode(index: number, initialValue: string): void {
        this.currentTagEditIndex = index;
        this.tagFocusIndex = index;
        this.tagEditFormControl.setValue(initialValue);
    }

    public onEscape(): void {
        this.currentTagEditIndex = -1;
    }

    public onSubmitTagEditChange(): void {
        this.state$.pipe(take(1), takeUntil(this.destroy$)).subscribe(state => {
            let newState: FormValue<string>[] = [...state];

            if (this.tagEditFormControl.value) {
                const oldValue: FormValue = state[this.currentTagEditIndex];
                const newValue: string = this.tagEditFormControl.value;

                // No changes were made, update state with same value
                if (oldValue.value === newValue) {
                    return this.state$.next(state);
                }

                newState = this.updateStateOnEdit(newState, oldValue, newValue);
            } else {
                // All characters were deleted, remove tag from state
                const oldValue: FormValue = state[this.currentTagEditIndex];
                newState.splice(this.currentTagEditIndex, 1);

                if (this.savedInput.some(formValue => formValue.value === oldValue.value)) {
                    newState.push(new FormValue(oldValue.value, FormValueState.UNSET));
                }
            }

            this.state$.next(newState);
        });
    }

    private updateStateOnEdit(oldState: FormValue<string>[], oldValue: FormValue, newValue: string): FormValue[] {
        const newState: FormValue<string>[] = oldState;

        const indexOfUnset: number = this.findIndexOfUnsetValue(newState, newValue);
        // By editing, the user has restored the original state.
        if (indexOfUnset !== -1) {
            newState.splice(indexOfUnset, 1);
        }

        // Old value is an original value. UNSET it.
        else if (this.savedInput.find(formValue => formValue.value === oldValue.value)) {
            newState.push(new FormValue(oldValue.value, FormValueState.UNSET));
        }

        newState[this.currentTagEditIndex] = new FormValue(newValue, newState[this.currentTagEditIndex].state);

        return newState;
    }

    private get currentFormValues(): FormValue<string>[] {
        return this.state$.value;
    }

    private get currentTags(): string[] {
        return this.currentFormValues.map(formValue => formValue.value);
    }

    private findIndexOfUnsetValue(formValues: FormValue[], value: string): number {
        return formValues.findIndex(formValue => formValue.value === value && formValue.state === FormValueState.UNSET);
    }
}
